import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Native Select Components
 * 
 * These components provide a native HTML select experience that works reliably
 * on iOS/mobile without the freeze issues that Radix UI Select has in Dialogs.
 * 
 * The API is compatible with Radix UI Select for easy migration.
 */

// Context to share state between Select components
interface SelectContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  options: Map<string, string>;
  registerOption: (value: string, label: string) => void;
  unregisterOption: (value: string) => void;
}

const SelectContext = React.createContext<SelectContextValue>({
  options: new Map(),
  registerOption: () => {},
  unregisterOption: () => {},
});

interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  children: React.ReactNode;
  required?: boolean;
}

const Select = ({ value, defaultValue, onValueChange, disabled, children }: SelectProps) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue || "");
  const [options, setOptions] = React.useState<Map<string, string>>(new Map());
  
  const actualValue = value !== undefined ? value : internalValue;
  
  const handleChange = React.useCallback((newValue: string) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  }, [value, onValueChange]);

  const registerOption = React.useCallback((optValue: string, label: string) => {
    setOptions(prev => {
      const next = new Map(prev);
      next.set(optValue, label);
      return next;
    });
  }, []);

  const unregisterOption = React.useCallback((optValue: string) => {
    setOptions(prev => {
      const next = new Map(prev);
      next.delete(optValue);
      return next;
    });
  }, []);

  return (
    <SelectContext.Provider value={{ 
      value: actualValue, 
      onValueChange: handleChange, 
      disabled,
      options,
      registerOption,
      unregisterOption,
    }}>
      <div className="relative">
        {children}
      </div>
    </SelectContext.Provider>
  );
};

// SelectValue - displays the current value or placeholder
interface SelectValueProps {
  placeholder?: string;
}

const SelectValue = ({ placeholder }: SelectValueProps) => {
  const { value, options } = React.useContext(SelectContext);
  
  const displayText = value ? options.get(value) || value : "";
  
  return (
    <span className={cn("block truncate", !value && "text-muted-foreground")}>
      {displayText || placeholder || "Select..."}
    </span>
  );
};

// SelectTrigger - renders the visible trigger element
interface SelectTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const SelectTrigger = React.forwardRef<HTMLDivElement, SelectTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const { disabled } = React.useContext(SelectContext);
    
    return (
      <div 
        ref={ref} 
        className={cn(
          "flex h-11 w-full items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm ring-offset-background",
          "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50",
          className
        )} 
        {...props}
      >
        {children}
        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
      </div>
    );
  }
);
SelectTrigger.displayName = "SelectTrigger";

// SelectContent renders the native select element overlayed on top of the trigger
interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
  position?: "popper" | "item-aligned";
}

const SelectContent = React.forwardRef<HTMLSelectElement, SelectContentProps>(
  ({ children, className, position, ...props }, ref) => {
    const { value, onValueChange, disabled, options } = React.useContext(SelectContext);
    
    return (
      <>
        {/* Render children (SelectItems) to register options */}
        <div className="hidden">{children}</div>
        
        {/* Native select overlay - positioned over the entire Select wrapper */}
        <select
          ref={ref}
          value={value || ""}
          onChange={(e) => onValueChange?.(e.target.value)}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          {...props}
        >
          <option value="" disabled>
            Select...
          </option>
          {Array.from(options.entries()).map(([optValue, label]) => (
            <option key={optValue} value={optValue}>
              {label}
            </option>
          ))}
        </select>
      </>
    );
  }
);
SelectContent.displayName = "SelectContent";

// SelectItem - registers itself with the options context
interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ value, children, disabled, className, ...props }, ref) => {
    const { registerOption, unregisterOption } = React.useContext(SelectContext);
    
    // Extract text content from children
    const label = React.useMemo(() => {
      if (typeof children === 'string') return children;
      if (typeof children === 'number') return String(children);
      if (React.isValidElement(children)) {
        const props = children.props as { children?: React.ReactNode };
        if (typeof props.children === 'string') return props.children;
      }
      // For complex children, try to get string representation
      return String(children);
    }, [children]);
    
    React.useEffect(() => {
      registerOption(value, label);
      return () => unregisterOption(value);
    }, [value, label, registerOption, unregisterOption]);
    
    // SelectItem doesn't render anything visible - it just registers the option
    return null;
  }
);
SelectItem.displayName = "SelectItem";

// SelectGroup - groups related options
interface SelectGroupProps {
  children: React.ReactNode;
}

const SelectGroup = ({ children }: SelectGroupProps) => {
  return <>{children}</>;
};

// SelectLabel - label for a group (not supported in native select but kept for API compat)
interface SelectLabelProps {
  children: React.ReactNode;
  className?: string;
}

const SelectLabel = React.forwardRef<HTMLDivElement, SelectLabelProps>(
  ({ children, className, ...props }, ref) => {
    return null;
  }
);
SelectLabel.displayName = "SelectLabel";

// SelectSeparator - visual separator (not used in native select)
const SelectSeparator = React.forwardRef<HTMLDivElement, { className?: string }>(
  ({ className, ...props }, ref) => {
    return null;
  }
);
SelectSeparator.displayName = "SelectSeparator";

// These are not used in native select but exported for API compatibility
const SelectScrollUpButton = () => null;
const SelectScrollDownButton = () => null;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
